import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ตัวเปิดดูรูปแบบ full-screen สไตล์เดียวกับ LINE — ปัดซ้าย/ขวาเปลี่ยนรูป, ปัดลงเพื่อปิด,
// แตะสองครั้ง/หมุนล้อ/นิ้วสองนิ้ว = ซูม, ลากเพื่อเลื่อนดูตอนซูมอยู่, แตะครั้งเดียวซ่อน/โชว์แถบเครื่องมือ
const MIN_SCALE = 1;
const MAX_SCALE = 6;
const DOUBLE_TAP_SCALE = 2.5;
const SWIPE_COMMIT_PX = 70; // ปัดเกินเท่านี้ = เปลี่ยนรูป
const DISMISS_COMMIT_PX = 130; // ปัดลงเกินเท่านี้ = ปิด
const TAP_MOVE_TOL = 10;
const TAP_TIME_MS = 260;
const DOUBLE_TAP_MS = 300;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

function filenameFromUrl(url) {
  try {
    const cleaned = String(url || '').split('?')[0];
    return decodeURIComponent(cleaned.substring(cleaned.lastIndexOf('/') + 1)) || 'image';
  } catch (e) {
    return 'image';
  }
}

export default function ImageLightbox({
  open,
  images = [],
  index = 0,
  onIndexChange,
  onClose,
}) {
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const count = list.length;
  const safeIndex = clamp(index, 0, Math.max(count - 1, 0));

  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [rotate, setRotate] = useState(0);
  const [dragX, setDragX] = useState(0); // ปัดแนวนอน (ตอน scale === 1)
  const [dragY, setDragY] = useState(0); // ปัดลงเพื่อปิด (ตอน scale === 1)
  const [dragging, setDragging] = useState(false);
  const [chrome, setChrome] = useState(true); // โชว์แถบเครื่องมือด้านบน

  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const gesture = useRef(null);
  const pointers = useRef(new Map());
  const lastTap = useRef(0);

  const resetView = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
    setRotate(0);
    setDragX(0);
    setDragY(0);
  }, []);

  // รีเซ็ตทุกครั้งที่เปลี่ยนรูป / เปิดใหม่
  useEffect(() => {
    resetView();
  }, [safeIndex, open, resetView]);

  // ระยะเลื่อนสูงสุดที่ยอมให้ pan ได้ตอนซูม (กันเลื่อนรูปหลุดจอ)
  const panBounds = useCallback(
    (s) => {
      const el = imgRef.current;
      const cont = containerRef.current;
      if (!el || !cont) return { x: 0, y: 0 };
      const rect = el.getBoundingClientRect();
      const fittedW = rect.width / (scale || 1);
      const fittedH = rect.height / (scale || 1);
      return {
        x: Math.max(0, (fittedW * s - cont.clientWidth) / 2),
        y: Math.max(0, (fittedH * s - cont.clientHeight) / 2),
      };
    },
    [scale],
  );

  const goto = useCallback(
    (next) => {
      if (next < 0 || next >= count || next === safeIndex) return;
      onIndexChange?.(next);
    },
    [count, safeIndex, onIndexChange],
  );

  const zoomAtPoint = useCallback(
    (nextScale, clientX, clientY) => {
      const cont = containerRef.current;
      if (!cont) return;
      const rect = cont.getBoundingClientRect();
      const cx = clientX - rect.left - rect.width / 2;
      const cy = clientY - rect.top - rect.height / 2;
      const s1 = clamp(nextScale, MIN_SCALE, MAX_SCALE);
      const ratio = s1 / (scale || 1);
      if (s1 === 1) {
        setScale(1);
        setTx(0);
        setTy(0);
        return;
      }
      const b = panBounds(s1);
      setScale(s1);
      setTx(clamp((tx - cx) * ratio + cx, -b.x, b.x));
      setTy(clamp((ty - cy) * ratio + cy, -b.y, b.y));
    },
    [scale, tx, ty, panBounds],
  );

  // ── wheel zoom (desktop) ── ใช้ native listener แบบ non-passive เพื่อ preventDefault ได้
  useEffect(() => {
    const cont = containerRef.current;
    if (!cont || !open) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.85 : 1.18;
      zoomAtPoint(scale * factor, e.clientX, e.clientY);
    };
    cont.addEventListener('wheel', onWheel, { passive: false });
    return () => cont.removeEventListener('wheel', onWheel);
  }, [open, scale, zoomAtPoint]);

  // ── keyboard ──
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight' && scale === 1) goto(safeIndex + 1);
      else if (e.key === 'ArrowLeft' && scale === 1) goto(safeIndex - 1);
      else if (e.key === '0') resetView();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, scale, safeIndex, goto, resetView]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        mode: 'pinch',
        startDist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        baseScale: scale,
      };
      return;
    }
    gesture.current = {
      mode: scale > 1 ? 'pan' : 'idle',
      startX: e.clientX,
      startY: e.clientY,
      baseTx: tx,
      baseTy: ty,
      startTime: Date.now(),
      moved: 0,
    };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;

    if (g.mode === 'pinch' && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const next = clamp((g.baseScale * dist) / g.startDist, MIN_SCALE, MAX_SCALE);
      setScale(next);
      if (next === 1) {
        setTx(0);
        setTy(0);
      }
      return;
    }

    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    g.moved = Math.max(g.moved, Math.hypot(dx, dy));

    if (g.mode === 'idle') {
      if (Math.hypot(dx, dy) < 8) return;
      g.mode = Math.abs(dx) > Math.abs(dy) ? 'swipe' : 'dismiss';
    }

    if (g.mode === 'pan') {
      const bnd = panBounds(scale);
      setTx(clamp(g.baseTx + dx, -bnd.x, bnd.x));
      setTy(clamp(g.baseTy + dy, -bnd.y, bnd.y));
    } else if (g.mode === 'swipe') {
      const atStart = safeIndex === 0 && dx > 0;
      const atEnd = safeIndex === count - 1 && dx < 0;
      setDragX(atStart || atEnd ? dx * 0.35 : dx);
    } else if (g.mode === 'dismiss') {
      setDragY(dy);
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;

    if (pointers.current.size > 0) {
      // ยกนิ้วออกจาก pinch เหลือนิ้วเดียว → เปลี่ยนเป็น pan/idle ต่อ
      if (g && g.mode === 'pinch') {
        const pt = [...pointers.current.values()][0];
        gesture.current = {
          mode: scale > 1 ? 'pan' : 'idle',
          startX: pt.x,
          startY: pt.y,
          baseTx: tx,
          baseTy: ty,
          startTime: Date.now(),
          moved: 0,
        };
      }
      return;
    }

    setDragging(false);
    if (!g) return;

    if (g.mode === 'swipe') {
      if (dragX <= -SWIPE_COMMIT_PX) goto(safeIndex + 1);
      else if (dragX >= SWIPE_COMMIT_PX) goto(safeIndex - 1);
      setDragX(0);
    } else if (g.mode === 'dismiss') {
      if (Math.abs(dragY) >= DISMISS_COMMIT_PX) onClose?.();
      setDragY(0);
    } else if (g.mode === 'pinch') {
      if (scale <= 1.02) {
        setScale(1);
        setTx(0);
        setTy(0);
      }
    } else if (g.moved < TAP_MOVE_TOL && Date.now() - g.startTime < TAP_TIME_MS) {
      const now = Date.now();
      if (now - lastTap.current < DOUBLE_TAP_MS) {
        lastTap.current = 0;
        setChrome((c) => !c); // ยกเลิก toggle จากแตะครั้งแรก
        if (scale > 1) resetView();
        else zoomAtPoint(DOUBLE_TAP_SCALE, e.clientX, e.clientY);
      } else {
        lastTap.current = now;
        setChrome((c) => !c);
      }
    }
    gesture.current = null;
  };

  if (!open || count === 0) return null;

  const dismissRatio = Math.min(Math.abs(dragY) / 500, 1);

  const stop = (e) => e.stopPropagation();

  return (
    <Modal open={open} onClose={onClose}>
      {/* ตัวจัดกึ่งกลาง — คลิกนอกกรอบ popup เพื่อปิด */}
      <Box
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2 },
        }}
      >
        {/* กรอบ popup — ตัวเปิดดูรูป (ไม่เต็มจอ) */}
        <Box
          ref={containerRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          sx={{
            position: 'relative',
            width: { xs: '100%', md: '88vw' },
            maxWidth: 1200,
            height: { xs: '92vh', md: '88vh' },
            bgcolor: '#111',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            touchAction: 'none',
            userSelect: 'none',
            outline: 'none',
            opacity: 1 - dismissRatio * 0.5,
            transform: `translateY(${scale === 1 ? dragY * 0.35 : 0}px) scale(${
              1 - dismissRatio * 0.06
            })`,
            transition: dragging
              ? 'none'
              : 'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity 200ms ease',
          }}
        >
          {/* Track: สไลด์ทุกรูปเรียงแนวนอน เลื่อนด้วย translateX */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            transform: `translate3d(calc(${-safeIndex * 100}% + ${dragX}px), 0, 0)`,
            transition: dragging
              ? 'none'
              : 'transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          }}
        >
          {list.map((src, i) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              sx={{
                flex: '0 0 100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                px: 1,
              }}
            >
              <Box
                component="img"
                ref={i === safeIndex ? imgRef : undefined}
                src={src}
                alt={`รูป ${i + 1}`}
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                  transform:
                    i === safeIndex
                      ? `translate3d(${tx}px, ${ty}px, 0) scale(${scale}) rotate(${rotate}deg)`
                      : 'none',
                  transition: dragging ? 'none' : 'transform 200ms ease',
                }}
              />
            </Box>
          ))}
        </Box>

        {/* แถบเครื่องมือด้านบน — ซ่อน/โชว์ด้วยการแตะ */}
        <Box
          onPointerDown={stop}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 1,
            color: '#fff',
            background: 'linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0))',
            opacity: chrome ? 1 : 0,
            pointerEvents: chrome ? 'auto' : 'none',
            transition: 'opacity 200ms ease',
          }}
        >
          <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Box sx={{ flex: 1, textAlign: 'center', fontSize: '0.9rem', fontWeight: 600 }}>
            {count > 1 ? `${safeIndex + 1} / ${count}` : ''}
          </Box>
          <IconButton
            onClick={() => setRotate((r) => (r + 90) % 360)}
            sx={{ color: '#fff' }}
            aria-label="rotate"
          >
            <RotateRightIcon />
          </IconButton>
          <IconButton
            component="a"
            href={list[safeIndex]}
            download={filenameFromUrl(list[safeIndex])}
            target="_blank"
            rel="noreferrer"
            sx={{ color: '#fff' }}
            aria-label="download"
          >
            <DownloadIcon />
          </IconButton>
        </Box>

        {/* ลูกศรซ้าย/ขวา — เฉพาะจอใหญ่ (มือถือใช้ปัด) และตอนไม่ได้ซูม */}
        {count > 1 && scale === 1 && safeIndex > 0 && (
          <IconButton
            onPointerDown={stop}
            onClick={() => goto(safeIndex - 1)}
            aria-label="previous"
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.35)',
              opacity: chrome ? 1 : 0,
              transition: 'opacity 200ms ease',
              display: { xs: 'none', md: 'inline-flex' },
              '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
        {count > 1 && scale === 1 && safeIndex < count - 1 && (
          <IconButton
            onPointerDown={stop}
            onClick={() => goto(safeIndex + 1)}
            aria-label="next"
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.35)',
              opacity: chrome ? 1 : 0,
              transition: 'opacity 200ms ease',
              display: { xs: 'none', md: 'inline-flex' },
              '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
        </Box>
      </Box>
    </Modal>
  );
}
